import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart, brl } from "../context/CartContext";
import { BrandName } from "./BrandName";

export const effectivePrice = (p) => (p.sale_price != null && p.sale_price < p.price ? p.sale_price : p.price);

export const inStock = (p) => {
  const variants = p.variants || [];
  if (variants.length) return variants.some((v) => (v.stock || 0) > 0);
  return (p.stock || 0) > 0;
};

export const ProductCard = ({ p, i = 0 }) => {
  const { add } = useCart();
  const out = !inStock(p);
  const hasVariants = (p.variants || []).length > 0;
  return (
    <motion.article
      data-testid={`product-card-${p.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: "easeOut", delay: (i % 2) * 0.12 }}
      className="group border-b border-military/15 pb-8"
    >
      <div className="relative overflow-hidden rounded-lg bg-offwhite aspect-[4/3]">
        <div className="absolute inset-0 spotlight opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10 pointer-events-none" />
        <Link to={`/produto/${p.slug}`} data-testid={`product-link-${p.slug}`} aria-label={p.name} className="absolute inset-0 z-10">
          <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        </Link>
        {p.tag && (
          <span className="absolute top-4 left-4 z-20 bg-baby text-military-dark text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">
            {p.tag}
          </span>
        )}
        {out && (
          <span data-testid={`out-of-stock-${p.slug}`} className="absolute top-4 right-4 z-20 bg-military-dark text-offwhite text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">
            Esgotado
          </span>
        )}
        {!out && (hasVariants ? (
          <Link
            to={`/produto/${p.slug}`}
            data-testid={`add-to-cart-${p.slug}`}
            className="absolute bottom-4 right-4 z-20 flex items-center gap-2 rounded-full bg-military-dark text-offwhite px-5 py-3 text-xs uppercase tracking-widest font-semibold opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 hover:bg-baby hover:text-military-dark"
          >
            <Plus size={14} /> Escolher sabor
          </Link>
        ) : (
          <button
            data-testid={`add-to-cart-${p.slug}`}
            onClick={() => { add({ ...p, price: effectivePrice(p) }); toast.success(`${p.name} adicionado à sacola`); }}
            className="absolute bottom-4 right-4 z-20 flex items-center gap-2 rounded-full bg-military-dark text-offwhite px-5 py-3 text-xs uppercase tracking-widest font-semibold opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 hover:bg-baby hover:text-military-dark"
          >
            <Plus size={14} /> Adicionar
          </button>
        ))}
      </div>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-military font-semibold mb-2">{p.brand ? <><BrandName name={p.brand} /> · </> : ""}{p.category}</p>
          <h3 className="font-serif text-2xl md:text-3xl text-military-dark leading-tight">
            <Link to={`/produto/${p.slug}`} className="hover:text-military transition-colors">{p.name}</Link>
          </h3>
          <p className="mt-2 text-sm text-military-dark/60 leading-relaxed line-clamp-2">{p.description}</p>
        </div>
        <div className="pt-6 text-right">
          {p.sale_price != null && p.sale_price < p.price && (
            <p className="text-sm text-military-dark/40 line-through whitespace-nowrap">{brl(p.price)}</p>
          )}
          <p data-testid={`price-${p.slug}`} className="font-sans font-semibold text-lg text-military-dark whitespace-nowrap">{brl(effectivePrice(p))}</p>
        </div>
      </div>
    </motion.article>
  );
};
